/**
 * The one place that asks iOS for orientation access.
 *
 * iOS 13+ hides DeviceOrientationEvent behind a permission call that must
 * happen while a user gesture is still active. Explore Mode is a lazy chunk,
 * so an ask fired from its mount effect arrives after the chunk download —
 * long past the activation window — and Safari rejects it. Callers therefore
 * prime this from the tap that opens the mode, and the hooks await the same
 * cached promise so the user is only ever asked once.
 *
 * Consumers: App.tsx (the three Explore entry points), useExploreMode,
 * useCompass.
 */

export type CompassPermission = "granted" | "denied" | "prompt" | "unsupported";

/** iOS Safari ships requestPermission on the constructor; not in lib.dom. */
type OrientationCtor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

function orientationCtor(): OrientationCtor | null {
  if (typeof window === "undefined") return null;
  const ctor = (window as unknown as { DeviceOrientationEvent?: OrientationCtor }).DeviceOrientationEvent;
  return ctor ?? null;
}

// Module-level so the answer survives Explore Mode unmounting, and so a
// second entry point never raises a second dialog.
let inflight: Promise<CompassPermission> | null = null;

/**
 * Ask once, then hand every later caller the same answer.
 *
 * A rejection is not a refusal — it is Safari saying the call came from
 * outside a gesture. That clears the cache so a genuine tap can retry, and
 * resolves to "prompt" so the UI keeps offering the button.
 */
export function requestCompassPermission(): Promise<CompassPermission> {
  const ctor = orientationCtor();
  if (!ctor) return Promise.resolve("unsupported");
  // Android and desktop fire orientation events without asking.
  if (typeof ctor.requestPermission !== "function") return Promise.resolve("granted");
  if (!inflight) {
    inflight = ctor.requestPermission()
      .then((res): CompassPermission => (res === "granted" ? "granted" : "denied"))
      .catch((): CompassPermission => { inflight = null; return "prompt"; });
  }
  return inflight;
}

/**
 * Fire-and-forget version for click handlers.
 *
 * Called from the tap that opens Explore Mode, so the ask lands inside the
 * gesture rather than after the lazy chunk has downloaded. The result is
 * cached, so the hook that mounts a moment later reads it without prompting
 * again.
 */
export function primeCompassPermission(): void {
  void requestCompassPermission();
}

/** Test seam — there is no way to un-ask iOS from inside a test run. */
export function resetCompassPermissionForTest(): void {
  inflight = null;
}
